// src/pages/AssignQuestions.jsx
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Visibility as EyeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import api from '../api';   // ← shared Axios instance that injects JWT
import { getRoleFromToken } from '../utils/auth';

// ─────────────────────────────────────────────────────────────────────────────
function AssignQuestions() {
  const navigate = useNavigate();

  // ─── Loading state ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setProgress((p) => (p >= 90 ? p : p + 10)), 200);
    return () => clearInterval(t);
  }, [loading]);

  // ─── Role-based visibility ────────────────────────────────────────────────
  const [currentRole, setCurrentRole] = useState(null);
  const isSuperAdmin = currentRole === 'superadmin';

  // ─── Server data ──────────────────────────────────────────────────────────
  const [questions,   setQuestions]   = useState([]);
  const [qaList,      setQaList]      = useState([]);
  const [teacherList, setTeacherList] = useState([]);

  // ─── Filters & selections ─────────────────────────────────────────────────
  const [selectedQa,      setSelectedQa]      = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [subjectFilter,   setSubjectFilter]   = useState('All');
  const [selectedIds,     setSelectedIds]     = useState([]);   // DataGrid selection

  // ─── UI feedback ─────────────────────────────────────────────────────────
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ─── Modal state ─────────────────────────────────────────────────────────
  const [openModal,   setOpenModal]   = useState(false);
  const [openSub,     setOpenSub]     = useState(false);
  const [viewQuestion,setViewQuestion]= useState(null);

  // ─────────────────────────────────────────────────────────────────────────
  // initial fetches
  useEffect(() => {
    const r = getRoleFromToken();
    setCurrentRole(r);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setProgress(30);
        await Promise.all([
          fetchQaList(),
          fetchTeacherList(),
          fetchQuestions(),
        ]);
        setProgress(100);
        setTimeout(() => setLoading(false), 400);
      } catch {
        setError('Failed to load data.');
        setLoading(false);
      }
    })();
  }, []);

  // 1) QA list
  const fetchQaList = async () => {
    try {
      const { data } = await api.get('/api/users/qaList');
      setQaList(data);
    } catch {
      setError('Failed to fetch QA users.');
    }
  };

  // 2) Teacher list
  const fetchTeacherList = async () => {
    try {
      const { data } = await api.get('/api/users/teacherList');
      setTeacherList(data);
    } catch {
      setError('Failed to fetch teachers.');
    }
  };

  // 3) Assignable questions
  const fetchQuestions = async () => {
    try {
      setError('');
      const { data } = await api.get('/api/questions/assignable');
      setQuestions(data);
    } catch {
      setError('Failed to fetch questions.');
    }
  };

  // 4) client-side filtering
  const rows = questions.filter(q => {
    if (subjectFilter !== 'All' && q.subject !== subjectFilter)       return false;
    if (selectedTeacher && q.teacherName !== selectedTeacher)         return false;
    return true;
  });

  // 5) DataGrid columns
  const columns = [
    {
      field: 'questionSnippet',
      headerName: 'Question',
      flex: 1,
      renderCell: params => {
        const html = params.row.question || '';
        const hasImage = html.includes('<img');
        const fullText = html.replace(/<[^>]+>/g, '');
        const words = fullText.trim().split(/\s+/);
        const snippet = isSuperAdmin 
          ? fullText.slice(0, 60)
          : (words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : ''));
        return (
          <Box>
            {Array.isArray(params.row.subQuestions) && params.row.subQuestions.length > 0 && (
              <Box sx={{ display:'inline-block', bgcolor:'cyan', color:'black', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.75rem' }}>
                SubQ
              </Box>
            )}
            {hasImage && (
              <Box sx={{ display:'inline-block', bgcolor:'#ffcc00', color:'black', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.75rem' }}>
                Image
              </Box>
            )}
            <span>{snippet || '...'}</span>
          </Box>
        );
      },
    },
    { field: 'teacherName', headerName: 'Teacher', width: 150 },
    { field: 'subject',     headerName: 'Subject', width: 120 },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 120,
      renderCell: ({ value }) => {
        if (!value) return '—';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return '—';
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric' 
          });
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: params => {
        const st = params.value;
        let bg = 'orange';
        if      (st === 'approved') bg = 'green';
        else if (st === 'rejected') bg = 'red';
        else if (st === 'draft')    bg = '#9e9e9e';
        return (
          <Box sx={{ bgcolor:bg, color:'white', borderRadius:1, px:1, py:0.25, width:'100%', textAlign:'center' }}>
            {st.charAt(0).toUpperCase()+st.slice(1)}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: params => (
        <Button variant="outlined" size="small" onClick={() => openViewer(params.row)} startIcon={<EyeIcon />}>
          View
        </Button>
      ),
    },
  ];

  // 6) Assign questions
  const handleAssign = async () => {
    try {
      if (!selectedQa)                  return setError('Please select a QA first.');
      if (selectedIds.length === 0)     return setError('No questions selected.');

      setError(''); setSuccess('');
      await api.post('/api/questions/assign', {
        questionIds: selectedIds,
        qaId: selectedQa,
      });
      setSuccess('Assigned questions successfully!');
      fetchQuestions();
      setSelectedIds([]);
    } catch {
      setError('Failed to assign questions.');
    }
  };

  // ─── View helpers ────────────────────────────────────────────────────────
  const openViewer = row => { setViewQuestion(row); setOpenModal(true); };
  const closeViewer = () => { setViewQuestion(null); setOpenModal(false); setOpenSub(false); };

  // logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Loading screen
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-100px',
            right: '-100px',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            bottom: '-50px',
            left: '-50px',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: 'white',
              borderRadius: 4,
              p: 6,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.5px',
                }}
              >
                CBT
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                mb: 1,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Loading Questions
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#7f8c8d',
                mb: 4,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Preparing assignment panel...
            </Typography>

            <Box sx={{ position: 'relative', mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e7ff',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>
            
            <Typography
              variant="body2"
              sx={{
                color: '#667eea',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {progress}%
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        mb: { xs: 3, md: 4 },
        pb: 3,
        borderBottom: '3px solid',
        borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
        gap: { xs: 2, md: 0 },
      }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
            Assign Questions to QA
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Select questions and assign them to Quality Assurance officers
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.2,
            fontWeight: 600,
            borderWidth: 2,
            borderColor: '#dc3545',
            color: '#dc3545',
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', md: '1rem' },
            '&:hover': {
              borderWidth: 2,
              borderColor: '#c82333',
              background: '#fff5f5',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Logout
        </Button>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb:2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }}>{success}</Alert>}

      {/* Filters Card */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          p: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600, color: '#2c3e50', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Selection & Filters
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* QA dropdown */}
          <FormControl fullWidth>
            <InputLabel id="qa-select">Select QA</InputLabel>
            <Select 
              labelId="qa-select" 
              label="Select QA" 
              value={selectedQa} 
              onChange={e=>setSelectedQa(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {qaList.map(qa=>(
                <MenuItem key={qa._id} value={qa._id}>{qa.username}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* Teacher filter */}
            <FormControl fullWidth>
              <InputLabel id="teacher-filter">Filter by Teacher</InputLabel>
              <Select 
                labelId="teacher-filter" 
                label="Teacher Filter" 
                value={selectedTeacher} 
                onChange={e=>setSelectedTeacher(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="">All</MenuItem>
                {teacherList.map(t=>(
                  <MenuItem key={t._id} value={t.username}>{t.username}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subject filter */}
            <FormControl fullWidth>
              <InputLabel id="subject-filter">Filter by Subject</InputLabel>
              <Select 
                labelId="subject-filter" 
                label="Subject Filter" 
                value={subjectFilter} 
                onChange={e=>setSubjectFilter(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                {['All','Understanding of Holy Quran','Mathematics','Philosophy', 'Philosophy 1','Ethics','English','Computer','Computer Science','IT','Physics','Chemistry','General Knowledge','General Science','Islamiat','Medical','Education','Business Administration','Commerce','Research/ORIC','Finance','Examinations','Civil','Electrical Engineering','Security','QEC']
                  .map(sub=>(<MenuItem key={sub} value={sub}>{sub}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Questions Table */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Available Questions
          </Typography>
        </Box>
        <Box sx={{ height: { xs: 400, sm: 500, md: 550 }, width: '100%' }}>
          <DataGrid
            rows={rows.map(q=>({ ...q, questionSnippet:q.question }))}
            columns={columns}
            getRowId={row=>row._id}
            checkboxSelection
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={setSelectedIds}
            pageSize={10}
            rowsPerPageOptions={[10,25,50]}
            components={{ Toolbar: GridToolbar }}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
                fontSize: { xs: '0.875rem', md: '1rem' },
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', md: '1rem' },
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: '#f8f9ff',
              },
            }}
          />
        </Box>
      </Box>

      {/* Assign button */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
          {selectedIds.length} question{selectedIds.length !== 1 ? 's' : ''} selected
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleAssign}
          disabled={!selectedQa || selectedIds.length === 0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: 4,
            py: 1.2,
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
            },
            '&:disabled': {
              background: '#ccc',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Assign Selected Questions
        </Button>
      </Box>

      {/* Main viewer modal */}
      <Dialog 
        open={openModal} 
        onClose={closeViewer} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Question Details
        </DialogTitle>
        {viewQuestion && (
          <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
            {/* Info Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 2,
                borderLeft: '4px solid #667eea',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                flex: 1,
                minWidth: 200,
              }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                  Subject
                </Typography>
                <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                  {viewQuestion.subject}
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 2,
                borderLeft: '4px solid #11998e',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                flex: 1,
                minWidth: 200,
              }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                  Teacher
                </Typography>
                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                  {viewQuestion.teacherName}
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 2,
                borderLeft: '4px solid #f093fb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                flex: 1,
                minWidth: 200,
              }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                  Status
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: viewQuestion.status === 'approved' ? '#4caf50' : 
                         viewQuestion.status === 'rejected' ? '#f44336' : '#ff9800',
                  fontWeight: 700,
                  textTransform: 'capitalize'
                }}>
                  {viewQuestion.status}
                </Typography>
              </Box>
            </Box>

            {/* Question Text */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Question
              </Typography>
              {isSuperAdmin ? (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: viewQuestion.question }}
                />
              ) : (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  {(() => {
                    const fullText = (viewQuestion.question || '').replace(/<[^>]+>/g, '');
                    const words = fullText.trim().split(/\s+/);
                    return words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : '');
                  })()}
                </Box>
              )}
            </Box>

            {/* Options */}
            {viewQuestion.options?.length > 0 && (
              <Box sx={{ 
                bgcolor: 'white', 
                p: 3, 
                borderRadius: 2,
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                  Answer Options
                </Typography>
                {isSuperAdmin ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {viewQuestion.options.map((opt, i) => {
                      const isCorrect = opt === viewQuestion.correctAnswer;
                      return (
                        <Box
                          key={i}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                            bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ 
                            minWidth: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}>
                            {String.fromCharCode(65 + i)}
                          </Box>
                          <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                            {isCorrect && (
                              <Typography variant="caption" sx={{ 
                                display: 'block',
                                color: '#4caf50',
                                fontWeight: 700,
                                mb: 0.5,
                              }}>
                                ✓ Correct Answer
                              </Typography>
                            )}
                            <Box dangerouslySetInnerHTML={{ __html: opt }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107', color: '#856404', fontWeight: 500 }}>
                    Answer options are hidden for Admin role.
                  </Box>
                )}
              </Box>
            )}

            {/* Sub Questions Button */}
            {viewQuestion.subQuestions?.length > 0 && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setOpenSub(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0e8274 0%, #2fd968 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View {viewQuestion.subQuestions.length} Sub Question{viewQuestion.subQuestions.length > 1 ? 's' : ''}
              </Button>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={closeViewer}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-questions modal */}
      <Dialog 
        open={openSub} 
        onClose={() => setOpenSub(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Sub Questions ({viewQuestion?.subQuestions?.length || 0})
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          {viewQuestion?.subQuestions?.map((sq, idx) => (
            <Box 
              key={idx} 
              sx={{ 
                bgcolor: 'white',
                borderRadius: 2,
                p: 3,
                mb: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #11998e',
              }}
            >
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: '#2c3e50',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  bgcolor: '#11998e',
                  color: 'white',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                }}>
                  {idx + 1}
                </Box>
                Sub Question {idx + 1}
              </Typography>
              
              {isSuperAdmin ? (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: sq.question }}
                />
              ) : (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  {(() => {
                    const fullText = (sq.question || '').replace(/<[^>]+>/g, '');
                    const words = fullText.trim().split(/\s+/);
                    return words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : '');
                  })()}
                </Box>
              )}

              {sq.options?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1.5 }}>
                    Answer Options
                  </Typography>
                  {isSuperAdmin ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      {sq.options.map((opt, i) => {
                        const isCorrect = opt === sq.correctAnswer;
                        return (
                          <Box
                            key={i}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                              bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ 
                              minWidth: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              flexShrink: 0,
                            }}>
                              {String.fromCharCode(65 + i)}
                            </Box>
                            <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                              {isCorrect && (
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  color: '#4caf50',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                  ✓ Correct Answer
                                </Typography>
                              )}
                              <Box dangerouslySetInnerHTML={{ __html: opt }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107', color: '#856404', fontWeight: 500 }}>
                      Answer options are hidden for Admin role.
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={() => setOpenSub(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AssignQuestions;
